/**
 * «Fødselsnummer» on a person's page (owner decision 2026-10-15; docs/platform-implementation/NATIONAL_ID.md).
 *
 * Drawn only when the mosque has the capability national_id and this staff member holds members.national_id
 * (lib/national-id `shows`). The number is MASKED by default — the birth-date part only, which staff already see.
 * «Vis» asks the server for it (POST …/reveal; the server audits every look) and shows it for 30 seconds, then masks it
 * again by itself; leaving the page forgets it. It is never stored in the browser, never logged, never copied anywhere
 * by the panel. «Før inn» / «Rett» records it from a paper form (the server checks both control digits and the
 * register's birth date, and refuses a number another person holds); «Slett» removes it (two clicks).
 */
import React, { useEffect, useState } from 'react';
import { errText, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { REVEAL_MS, SOURCE_NO, shown, typed } from '../lib/national-id';

export function NationalIdCard({ memberId }: { memberId: string }) {
  const path = `/members/${encodeURIComponent(memberId)}/national-id`;
  const st = useLoad<Body>(path);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [msg, say] = useMsg();
  // A revealed number masks itself again after REVEAL_MS, and is forgotten when the page goes.
  useEffect(() => { if (!revealed) return; const t = setTimeout(() => setRevealed(null), REVEAL_MS); return () => clearTimeout(t); }, [revealed]);
  useEffect(() => () => setRevealed(null), []);
  if (st.error) return <Card title="Fødselsnummer"><CardBody><LoadError error={st.error} retry={st.reload} /></CardBody></Card>;
  if (!st.data) return <Card title="Fødselsnummer"><CardBody><Loading /></CardBody></Card>;
  const s = st.data;
  const save = async () => {
    const r = await send(path, { method: 'PUT', body: JSON.stringify({ nationalId: value }) });
    setValue('');
    if (r.ok) { setEditing(false); setRevealed(null); say(r.body.message || 'Lagret.', 'ok'); void st.reload(); }
    else say(errText(r.body), 'err');
  };
  return (
    <Card title="Fødselsnummer" hint="Brukes til innmelding i trossamfunnsregisteret og søknad om statstilskudd."
      more={<p>Nummeret er kryptert og vises skjult. «Vis» henter det fra serveren, og hver gang noen ser hele nummeret, blir det logget. Det skjules igjen etter 30 sekunder. Skriv det aldri i notater eller e-post.</p>}>
      <CardBody>
        <div className="mperson-facts" aria-live="polite">
          <span data-testid="nid-value" style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em' }} dir="ltr">{shown(s, revealed)}</span>
          {s.registered && <Tag tone={s.verified ? 'green' : 'grey'}>{SOURCE_NO[s.source] || s.source}</Tag>}
        </div>
        <div className="acts" style={{ marginTop: 12 }}>
          {s.registered && (revealed
            ? <Btn ghost onClick={() => setRevealed(null)}>Skjul</Btn>
            : <Btn ghost onClick={async () => {
                const r = await post(`${path}/reveal`);
                if (r.ok && typeof r.body.nationalId === 'string') setRevealed(r.body.nationalId); else say(errText(r.body), 'err');
              }}>Vis</Btn>)}
          {!editing && <Btn ghost aria-expanded={false} onClick={() => { setEditing(true); say(''); }}>{s.registered ? 'Rett' : 'Før inn fra papirskjema'}</Btn>}
          {s.registered && <TwoStep label="Slett" confirmLabel="Klikk igjen: slett fødselsnummeret" onConfirm={async () => {
            const r = await send(path, { method: 'DELETE', body: '{}' });
            if (r.ok) { setRevealed(null); say(r.body.message || 'Slettet.', 'ok'); void st.reload(); } else say(errText(r.body), 'err');
          }} />}
        </div>
        {editing && <form onSubmit={(e) => { e.preventDefault(); void save(); }} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', marginTop: 12 }}>
          <Field label="Fødselsnummer (11 sifre)" note="Fra et signert papirskjema. Må stemme med fødselsdatoen i registeret." style={{ margin: 0, minWidth: 220 }}>
            {(id, describedBy) => <input id={id} aria-describedby={describedBy} value={value} inputMode="numeric" autoComplete="off" spellCheck={false}
              onChange={(e) => setValue(typed(e.target.value))} />}</Field>
          <Btn type="submit" ghost={false}>Lagre</Btn>
          <Btn ghost onClick={() => { setEditing(false); setValue(''); }}>Avbryt</Btn>
        </form>}
        <Msg text={msg.text} tone={msg.tone} />
      </CardBody>
    </Card>
  );
}
